import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import Map, { Marker, NavigationControl, Source, Layer } from "react-map-gl/mapbox";
import MapboxDraw from "mapbox-gl-draw";
import "mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "mapbox-gl/dist/mapbox-gl.css";
import {
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddLocationAltRoundedIcon from "@mui/icons-material/AddLocationAltRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import MyLocationRoundedIcon from "@mui/icons-material/MyLocationRounded";
import toast from "react-hot-toast";

const MAPBOX_TOKEN =
    process.env.REACT_APP_MAPBOX_PUBLIC_TOKEN ||
    process.env.REACT_APP_MAPBOX ||
    process.env.VITE_MAPBOX_PUBLIC_TOKEN ||
    "";

const DEFAULT_CENTER = [174.7644, -36.8484];
const DEFAULT_ZOOM = 12;
const GEOCODE_DEBOUNCE_MS = 350;

/**
 * A saved geo-fence: point (address + coords) or polygon (GeoJSON)
 * @typedef {{
 *   id: string;
 *   name: string;
 *   address?: string;
 *   type: 'point' | 'polygon';
 *   coordinates?: [number, number];
 *   geometry?: object; // GeoJSON geometry for polygon
 * }} SavedLocation
 */

/**
 * DrawControl: adds MapboxDraw to the map when map is ready
 */
function DrawControl({ mapRef, mapReady, onDrawCreate, onDrawUpdate, onDrawDelete }) {
    const addedRef = useRef(false);

    useEffect(() => {
        if (!mapReady) return;
        const map = mapRef?.current?.getMap?.();
        if (!map || !MAPBOX_TOKEN || addedRef.current) return;

        const draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
                polygon: true,
                trash: true,
            },
            defaultMode: "simple_select",
        });
        map.addControl(draw, "top-right");
        addedRef.current = true;

        const handleCreate = (e) => {
            onDrawCreate?.(e, draw);
        };
        const handleUpdate = (e) => {
            onDrawUpdate?.(e, draw);
        };
        const handleDelete = (e) => {
            onDrawDelete?.(e, draw);
        };

        map.on("draw.create", handleCreate);
        map.on("draw.update", handleUpdate);
        map.on("draw.delete", handleDelete);

        return () => {
            map.off("draw.create", handleCreate);
            map.off("draw.update", handleUpdate);
            map.off("draw.delete", handleDelete);
            if (map.getContainer()) {
                try {
                    map.removeControl(draw);
                } catch {
                    /* control may already be removed */
                }
            }
            addedRef.current = false;
        };
    }, [mapRef, mapReady, onDrawCreate, onDrawUpdate, onDrawDelete]);

    return null;
}

function generateId() {
    return `loc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export default function GeoFencingSection({
    merchantId: _merchantId,
    initialLocations = [],
    onSaveLocations,
    isSaving = false,
}) {
    const mapRef = useRef(null);
    const hasAutoCenteredRef = useRef(false);
    const [locations, setLocations] = useState(initialLocations);

    useEffect(() => {
        if (Array.isArray(initialLocations) && initialLocations.length >= 0) {
            setLocations(initialLocations);
        }
    }, [initialLocations]);
    const [searchQuery, setSearchQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    const [searching, setSearching] = useState(false);
    const [viewState, setViewState] = useState({
        longitude: DEFAULT_CENTER[0],
        latitude: DEFAULT_CENTER[1],
        zoom: DEFAULT_ZOOM,
    });
    const [currentLocation, setCurrentLocation] = useState(null);
    const [selectedPreview, setSelectedPreview] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [mapReady, setMapReady] = useState(false);
    const geocodeTimeoutRef = useRef(null);

    const flyTo = useCallback((lng, lat, zoom = 14) => {
        setViewState((prev) => ({
            ...prev,
            longitude: lng,
            latitude: lat,
            zoom,
        }));
        const map = mapRef?.current?.getMap?.();
        if (map) {
            map.flyTo({ center: [lng, lat], zoom, duration: 800 });
        }
    }, []);

    useEffect(() => {
        if (hasAutoCenteredRef.current) return;
        hasAutoCenteredRef.current = true;

        if (!navigator.geolocation) return;

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lng = pos.coords.longitude;
                const lat = pos.coords.latitude;
                setCurrentLocation({ longitude: lng, latitude: lat });
                flyTo(lng, lat, 16);
            },
            () => {
                // silent on initial load (user may deny permission)
            },
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 }
        );
    }, [flyTo]);

    const fetchPlaces = useCallback((query) => {
        if (!MAPBOX_TOKEN || !query.trim()) {
            setSuggestions([]);
            return;
        }
        setSearching(true);
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&limit=6&types=address,place,poi`;
        fetch(url)
            .then((res) => res.json())
            .then((data) => {
                setSuggestions(data.features || []);
                setSuggestionsOpen(true);
            })
            .catch(() => setSuggestions([]))
            .finally(() => setSearching(false));
    }, []);

    const onSearchChange = useCallback(
        (e) => {
            const q = e.target.value;
            setSearchQuery(q);
            if (geocodeTimeoutRef.current) clearTimeout(geocodeTimeoutRef.current);
            if (!q.trim()) {
                setSuggestions([]);
                setSuggestionsOpen(false);
                return;
            }
            geocodeTimeoutRef.current = setTimeout(
                () => fetchPlaces(q),
                GEOCODE_DEBOUNCE_MS
            );
        },
        [fetchPlaces]
    );

    const onSelectPlace = useCallback(
        (feature, addAsLocation = false) => {
            const [lng, lat] = feature.geometry?.coordinates || [];
            if (typeof lng !== "number" || typeof lat !== "number") return;
            const previewName =
                feature.text ||
                feature.place_name?.split(",")[0] ||
                `Location ${locations.length + 1}`;
            setSearchQuery(feature.place_name || "");
            setSuggestionsOpen(false);
            setSuggestions([]);
            flyTo(lng, lat);
            if (addAsLocation) {
                const name = previewName;
                setLocations((prev) => [
                    ...prev,
                    {
                        id: generateId(),
                        name,
                        address: feature.place_name || "",
                        type: "point",
                        coordinates: [lng, lat],
                    },
                ]);
                toast.success("Location added");
                setSelectedPreview(null);
            } else {
                setSelectedPreview({
                    longitude: lng,
                    latitude: lat,
                    name: previewName,
                });
            }
        },
        [flyTo, locations.length]
    );

    const addCurrentAsPoint = useCallback(() => {
        const { longitude, latitude } = viewState;
        const newLoc = {
            id: generateId(),
            name: `Location ${locations.length + 1}`,
            address: searchQuery || `Point at ${latitude.toFixed(5)}, ${longitude.toFixed(5)}`,
            type: "point",
            coordinates: [longitude, latitude],
        };
        setLocations((prev) => [...prev, newLoc]);
        toast.success("Location added");
    }, [viewState, searchQuery, locations.length]);

    const onDrawCreate = useCallback((e, _draw) => {
        const feats = e?.features || [];
        feats.forEach((f) => {
            if (f.geometry?.type === "Polygon") {
                const newLoc = {
                    id: generateId(),
                    name: `Fence ${locations.length + 1}`,
                    type: "polygon",
                    geometry: f.geometry,
                };
                setLocations((prev) => [...prev, newLoc]);
                toast.success("Geo-fence added");
            }
        });
    }, [locations.length]);

    const onDrawUpdate = useCallback((_e, _draw) => {
        // Optional: sync updated feature back to locations by id if we track feature ids
    }, []);

    const onDrawDelete = useCallback((_e) => {
        // Optional: remove from locations if we track draw feature ids
    }, []);

    const deleteLocation = useCallback((id) => {
        setLocations((prev) => prev.filter((l) => l.id !== id));
        setSelectedId((prev) => (prev === id ? null : prev));
        setSelectedPreview(null);
        toast.success("Location removed");
    }, []);

    const focusOnLocation = useCallback(
        (loc) => {
            setSelectedId(loc.id);
            setSelectedPreview(null);
            if (loc.type === "point" && loc.coordinates) {
                const [lng, lat] = loc.coordinates;
                flyTo(lng, lat, 16);
            } else if (loc.type === "polygon" && loc.geometry?.coordinates) {
                const coords = loc.geometry.coordinates[0];
                if (coords && coords.length) {
                    const lngs = coords.map((c) => c[0]);
                    const lats = coords.map((c) => c[1]);
                    const lng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
                    const lat = (Math.min(...lats) + Math.max(...lats)) / 2;
                    flyTo(lng, lat, 15);
                }
            }
        },
        [flyTo]
    );

    // GeoJSON for saved polygon fences (display only)
    const savedPolygonsGeoJSON = React.useMemo(() => {
        const features = locations
            .filter((l) => l.type === "polygon" && l.geometry)
            .map((l) => ({
                type: "Feature",
                id: l.id,
                properties: { name: l.name },
                geometry: l.geometry,
            }));
        return {
            type: "FeatureCollection",
            features,
        };
    }, [locations]);

    const useMyLocation = useCallback(() => {
        if (!navigator.geolocation) {
            toast.error("Geolocation not supported");
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lng = pos.coords.longitude;
                const lat = pos.coords.latitude;
                setCurrentLocation({ longitude: lng, latitude: lat });
                flyTo(lng, lat, 16);
                toast.success("Map centered on your location");
            },
            () => toast.error("Could not get your location"),
            { enableHighAccuracy: true }
        );
    }, [flyTo]);

    if (!MAPBOX_TOKEN) {
        return (
            <Box
                sx={{
                    p: 3,
                    textAlign: "center",
                    bgcolor: "grey.100",
                    borderRadius: 2,
                }}
            >
                <Typography color="text.secondary">
                    Mapbox token not configured. Add{" "}
                    <code>REACT_APP_MAPBOX_PUBLIC_TOKEN</code> to your .env file.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                height: { xs: "auto", md: "calc(100vh - 280px)" },
                minHeight: 420,
            }}
        >
            {/* Left: Search + Saved locations */}
            <Box
                sx={{
                    width: { xs: "100%", md: 360 },
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    overflow: "hidden",
                }}
            >
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 700,
                        color: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                    }}
                >
                    <PlaceRoundedIcon fontSize="small" />
                    Search &amp; save locations
                </Typography>

                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search address or place..."
                    value={searchQuery}
                    onChange={onSearchChange}
                    onFocus={() => suggestions.length > 0 && setSuggestionsOpen(true)}
                    onBlur={() => setTimeout(() => setSuggestionsOpen(false), 200)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRoundedIcon sx={{ color: "action.active" }} />
                            </InputAdornment>
                        ),
                        endAdornment: searching ? (
                            <InputAdornment position="end">
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: "50%",
                                        border: "2px solid",
                                        borderColor: "primary.main",
                                        borderTopColor: "transparent",
                                        animation: "spin 0.8s linear infinite",
                                        "@keyframes spin": {
                                            "100%": { transform: "rotate(360deg)" },
                                        },
                                    }}
                                />
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "background.paper",
                        },
                    }}
                />
                {suggestionsOpen && suggestions.length > 0 && (
                    <Box
                        sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 2,
                            overflow: "hidden",
                            bgcolor: "background.paper",
                            boxShadow: 2,
                            maxHeight: 220,
                            overflowY: "auto",
                        }}
                    >
                        {suggestions.map((f, i) => (
                            <Box
                                key={f.id || i}
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    "&:hover": { bgcolor: "action.hover" },
                                }}
                            >
                                <Box
                                    onClick={() => onSelectPlace(f)}
                                    sx={{
                                        flex: 1,
                                        cursor: "pointer",
                                        minWidth: 0,
                                    }}
                                >
                                    <Typography variant="body2" noWrap>
                                        {f.place_name}
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSelectPlace(f, true);
                                    }}
                                    sx={{ flexShrink: 0 }}
                                >
                                    Add
                                </Button>
                            </Box>
                        ))}
                    </Box>
                )}

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<MyLocationRoundedIcon />}
                        onClick={useMyLocation}
                        sx={{ borderRadius: 2 }}
                    >
                        My location
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddLocationAltRoundedIcon />}
                        onClick={addCurrentAsPoint}
                        sx={{
                            borderRadius: 2,
                            backgroundColor: "#2196f3 !important",
                            color: "#fff !important",
                            "& .MuiButton-startIcon": {
                                color: "inherit",
                            },
                            "&:hover": {
                                backgroundColor: "#1e88e5 !important",
                                color: "#fff !important",
                            },
                        }}
                    >
                        Add point here
                    </Button>
                </Box>

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                    <CategoryRoundedIcon sx={{ fontSize: 14 }} />
                    Draw a polygon on the map (tool in top-right) to add a geo-fence.
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Saved locations ({locations.length})
                    </Typography>
                    {typeof onSaveLocations === "function" && (
                        <Button
                            variant="contained"
                            size="small"
                            disabled={isSaving}
                            onClick={() => onSaveLocations(locations)}
                            sx={{
                                borderRadius: 2,
                                backgroundColor: "#2196f3 !important",
                                color: "#fff !important",
                                "& .MuiButton-startIcon": {
                                    color: "inherit",
                                },
                                "&:hover": {
                                    backgroundColor: "#1e88e5 !important",
                                    color: "#fff !important",
                                },
                            }}
                        >
                            {isSaving ? "Saving…" : "Save to database"}
                        </Button>
                    )}
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 120,
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                    }}
                >
                    {locations.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No locations yet. Search and add a point, or draw a polygon on the map.
                        </Typography>
                    ) : (
                        locations.map((loc) => (
                            <Box
                                key={loc.id}
                                onClick={() => focusOnLocation(loc)}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: 1,
                                    borderColor:
                                        selectedId === loc.id
                                            ? "primary.main"
                                            : "divider",
                                    bgcolor:
                                        selectedId === loc.id
                                            ? "primary.light"
                                            : "grey.50",
                                    cursor: "pointer",
                                    "&:hover": {
                                        bgcolor:
                                            selectedId === loc.id
                                                ? "primary.light"
                                                : "action.hover",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        justifyContent: "space-between",
                                        gap: 0.5,
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            noWrap
                                        >
                                            {loc.name}
                                        </Typography>
                                        {loc.address && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                noWrap
                                                display="block"
                                            >
                                                {loc.address}
                                            </Typography>
                                        )}
                                        <Chip
                                            label={loc.type}
                                            size="small"
                                            sx={{
                                                mt: 0.5,
                                                height: 20,
                                                fontSize: "0.7rem",
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ display: "flex" }}>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                focusOnLocation(loc);
                                            }}
                                            title="Focus on map"
                                        >
                                            <EditRoundedIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteLocation(loc.id);
                                            }}
                                            title="Remove"
                                        >
                                            <DeleteRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
            </Box>

            {/* Right: Map */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 320,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: 1,
                    borderColor: "divider",
                    position: "relative",
                }}
            >
                <Map
                    ref={mapRef}
                    {...viewState}
                    onMove={(evt) => setViewState(evt.viewState)}
                    onLoad={() => setMapReady(true)}
                    mapboxAccessToken={MAPBOX_TOKEN}
                    mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                    style={{ width: "100%", height: "100%" }}
                    attributionControl={false}
                >
                    <NavigationControl position="bottom-right" showCompass />
                    {currentLocation && (
                        <Source
                            id="current-location"
                            type="geojson"
                            data={{
                                type: "FeatureCollection",
                                features: [
                                    {
                                        type: "Feature",
                                        id: "current-location",
                                        properties: {},
                                        geometry: {
                                            type: "Point",
                                            coordinates: [currentLocation.longitude, currentLocation.latitude],
                                        },
                                    },
                                ],
                            }}
                        >
                            <Layer
                                id="current-location-ring"
                                type="circle"
                                paint={{
                                    "circle-radius": 14,
                                    "circle-color": "#0ea5e9",
                                    "circle-stroke-width": 3,
                                    "circle-stroke-color": "white",
                                    "circle-opacity": 0.55,
                                }}
                            />
                            <Layer
                                id="current-location-dot"
                                type="circle"
                                paint={{
                                    "circle-radius": 5,
                                    "circle-color": "#22c55e",
                                    "circle-stroke-width": 2,
                                    "circle-stroke-color": "white",
                                    "circle-opacity": 0.95,
                                }}
                            />
                        </Source>
                    )}
                    {selectedPreview && (
                        <Marker
                            longitude={selectedPreview.longitude}
                            latitude={selectedPreview.latitude}
                            anchor="bottom"
                        >
                            <Box
                                sx={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: "50%",
                                    bgcolor: "warning.main",
                                    border: "2px solid white",
                                    boxShadow: 3,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <PlaceRoundedIcon sx={{ color: "white", fontSize: 14 }} />
                            </Box>
                        </Marker>
                    )}
                    {savedPolygonsGeoJSON.features.length > 0 && (
                        <Source
                            id="saved-fences"
                            type="geojson"
                            data={savedPolygonsGeoJSON}
                        >
                            <Layer
                                id="saved-fences-fill"
                                type="fill"
                                paint={{
                                    "fill-color": "#0ea5e9",
                                    "fill-opacity": 0.2,
                                    "fill-outline-color": "#0ea5e9",
                                }}
                            />
                        </Source>
                    )}
                    <DrawControl
                        mapRef={mapRef}
                        mapReady={mapReady}
                        onDrawCreate={onDrawCreate}
                        onDrawUpdate={onDrawUpdate}
                        onDrawDelete={onDrawDelete}
                    />
                    {locations
                        .filter((l) => l.type === "point" && l.coordinates)
                        .map((loc) => (
                            <Marker
                                key={loc.id}
                                longitude={loc.coordinates[0]}
                                latitude={loc.coordinates[1]}
                                anchor="bottom"
                                onClick={() => focusOnLocation(loc)}
                            >
                                <Box
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "50%",
                                        bgcolor: "primary.main",
                                        border: "2px solid white",
                                        boxShadow: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <PlaceRoundedIcon
                                        sx={{ color: "white", fontSize: 16 }}
                                    />
                                </Box>
                            </Marker>
                        ))}
                </Map>
            </Box>
        </Box>
    );
}
